import { useEffect, useRef } from 'react';

export interface SyncPollingOptions {
  /** Consultar mientras sea true (corrida activa o recién pedida). */
  enabled: boolean;
  /** Milisegundos entre consultas (SYNC_POLL_INTERVAL_MS). */
  intervalMs: number;
  /** Una consulta; la siguiente se agenda cuando ésta termina (nunca se superponen). */
  onTick: () => Promise<void> | void;
}

/**
 * Polling encadenado con setTimeout: la próxima consulta se agenda al terminar la anterior, se detiene
 * cuando `enabled` pasa a false y al desmontar (sin consultas huérfanas ni intervalos acumulados).
 */
export const useSyncPolling = ({ enabled, intervalMs, onTick }: SyncPollingOptions): void => {
  const tickRef = useRef(onTick);
  tickRef.current = onTick;

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const schedule = () => {
      timer = setTimeout(async () => {
        if (cancelled) return;
        try {
          await tickRef.current();
        } catch {
          // un error de red no corta el seguimiento: se reintenta en la próxima vuelta
        }
        if (!cancelled) schedule();
      }, intervalMs);
    };
    schedule();
    return () => {
      cancelled = true;
      if (timer !== undefined) clearTimeout(timer);
    };
  }, [enabled, intervalMs]);
};

export default useSyncPolling;
